import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Check, X } from 'lucide-react'
import { v4 as uuidv4 } from 'uuid'
import { DEFAULT_FOLDER_ID } from '../../../../constants'
import useIsReadOnly from '../../../../hooks/useIsReadOnly'
import { useIsFeatureEnabled } from '../../../../services/api/feature-flags.hooks'
import {
    TGoalLog,
    useAchieveGoal,
    useArchiveGoal,
    useCreateGoal,
    useGetGoalRecurringTemplateLinks,
    useGetGoalTaskLinks,
    useGetGoals,
    useLinkRecurringTemplateToGoals,
    useLinkTaskToGoal,
    useLogGoalProgress,
    useReopenGoal,
    useUnarchiveGoal,
    useUpdateGoal,
} from '../../../../services/api/goals.hooks'
import { useCreateTask } from '../../../../services/api/tasks.hooks'
import { notifyReadOnlyRejection } from '../../../../utils/readOnly'
import { addGoalToTemplateHook } from '../../RecurringGoalHooks'
import { TGoal, TGoalPatch, TGoalTaskHandling, normalizeGoalEndDate } from '../../goalTypes'
import { timeframeLabelAfterEndDateEdit } from './propertyOptions'
import { TGoalDraft, buildGoalFromDraft, plannedTaskTitlesForDraft } from './scenario'

export type TIteration = 'coach' | 'scaffold' | 'mirror'

/**
 * Scaffold is the converged v1 flow (sync 2026-07-09). Coach and Mirror remain
 * browsable in lab mode only: load the app with `?lab` to get the full
 * three-way iteration switcher back.
 */
export const isLabMode = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('lab')

interface TGoalCreation {
    iteration: TIteration
    setIteration: (i: TIteration) => void
    isOnboarding: boolean
    setIsOnboarding: (b: boolean) => void
    /** Lab switch: show the empty-account path without deleting persisted goals. */
    freshAccount: boolean
    setFreshAccount: (b: boolean) => void
    createdGoals: TGoal[]
    allGoals: TGoal[]
    areGoalsLoading: boolean
    /** The goals request failed: the account's goal state is unknown, not empty. */
    areGoalsUnavailable: boolean
    createGoal: (draft: TGoalDraft) => TGoal
    /** Save a manual contribution; the server recomputes the week and status. */
    logGoalProgress: (goalId: string, log: Omit<TGoalLog, 'goalId' | 'id'>) => void
    isFlowOpen: boolean
    openFlow: () => void
    closeFlow: () => void
    consolidationDismissed: boolean
    dismissConsolidation: () => void
    mirrorGoalId: string | null
    setMirrorGoalId: (id: string | null) => void
    /** Save direct user edits to goal metadata; derived fields are still server-owned. */
    updateGoal: (goalId: string, patch: TGoalPatch, options?: TGoalUpdateOptions) => void
    /** Goal edits save as you type, so the details pane shows the same sync line task details does. */
    isSavingGoal: boolean
    goalSaveFailed: boolean
    /** The goal currently open in the RepairDialog — any surface can set this. */
    repairGoalId: string | null
    setRepairGoalId: (id: string | null) => void
    /**
     * Archive a goal, saying what becomes of its linked tasks. Recoverable: the goal keeps its row
     * and comes back with unarchiveGoal. Its contribution history does not — that is discarded and
     * is not restored, which is why the dialog says so before the user commits.
     */
    archiveGoal: (goalId: string, taskHandling: TGoalTaskHandling, options?: TArchiveGoalOptions) => void
    unarchiveGoal: (goalId: string) => void
    /** The goal currently open in the ArchiveGoalDialog — any surface can set this. */
    archiveGoalId: string | null
    /** Options travel with the id and are dropped when the dialog closes. */
    archiveGoalOptions: TArchiveGoalOptions | null
    setArchiveGoalId: (id: string | null, options?: TArchiveGoalOptions) => void
    /**
     * Mark a goal achieved, saying what becomes of its open tasks. Keeps its history; reopenGoal
     * puts it back in the active list. Options.onError lets the confirm stay open to retry.
     */
    achieveGoal: (goalId: string, taskHandling: TGoalTaskHandling, options?: TAchieveGoalOptions) => void
    isAchievingGoal: boolean
    reopenGoal: (goalId: string) => void
    /** The goal currently open in the AchieveGoalDialog — the details pane and the review set this. */
    achieveGoalId: string | null
    achieveGoalOptions: TAchieveGoalOptions | null
    setAchieveGoalId: (id: string | null, options?: TAchieveGoalOptions) => void
    /**
     * The goal achieved in this session, until the list has shown it: the Accomplished section opens
     * for it and focuses its card once, then clears it.
     */
    justAchievedGoalId: string | null
    clearJustAchievedGoalId: () => void
    /**
     * Task ↔ goal links (Linear model: a task belongs to at most one goal). Keyed by task id
     * (optimistic ids included). Lives here so every surface — composer, task details, goal
     * details, list rows — reads and writes the same association.
     */
    taskGoalLinks: Record<string, string>
    /** Prevent another goal choice while the queued link request is still settling. */
    isLinkingTaskToGoal: boolean
    /** Pass optimisticId for a task still being created; the link is sent once it has a real id. */
    linkTaskToGoal: (taskId: string, goalId: string | null, optimisticId?: string, options?: TGoalLinkOptions) => void
    goalIdForTask: (taskId: string, optimisticId?: string) => string | undefined
    /**
     * Create a task in the default folder already linked to a goal, and return its optimistic id.
     * The link is queued behind the create, so a create that fails takes its link with it.
     */
    createLinkedTask: (title: string, goalId: string, options?: TLinkedTaskOptions) => string
    resetDemo: () => void
}

/** How the weekly review opens the archive dialog: closing a goal as finished, and hearing back. */
export type TArchiveGoalOptions = {
    reason?: 'done'
    /** Runs once the archive is saved, so a caller can record what it did only after it happened. */
    onArchived?: () => void
}
/** How a caller hears back from the achieve dialog — the review records its answer only after. */
export type TAchieveGoalOptions = {
    onAchieved?: () => void
    onError?: () => void
}
type TGoalUpdateOptions = {
    onSuccess?: () => void
    onError?: () => void
    onSettled?: () => void
}
type TGoalLinkOptions = {
    onSuccess?: (taskId: string) => void
    onError?: (taskId: string) => void
}

type TLinkedTaskOptions = {
    /** The task was never created. */
    onCreateError?: () => void
    /** The task exists but is not linked to the goal. */
    onLinkError?: (taskId: string) => void
}

const Ctx = createContext<TGoalCreation | null>(null)

export const useGoalCreation = () => {
    const ctx = useContext(Ctx)
    if (!ctx) throw new Error('useGoalCreation must be used inside GoalCreationProvider')
    return ctx
}

/** Same as useGoalCreation, but null outside the provider (e.g. focus mode) so shared rows can render anywhere. */
export const useOptionalGoalCreation = () => useContext(Ctx)

export const GoalCreationProvider = ({ children }: { children: ReactNode }) => {
    const navigate = useNavigate()
    const areGoalsEnabled = useIsFeatureEnabled('goals')
    const goalsQuery = useGetGoals()
    const taskLinksQuery = useGetGoalTaskLinks()
    const recurringTemplateLinksQuery = useGetGoalRecurringTemplateLinks()
    const createGoalMutation = useCreateGoal()
    const updateGoalMutation = useUpdateGoal()
    const logGoalProgressMutation = useLogGoalProgress()
    const linkTaskToGoalMutation = useLinkTaskToGoal()
    const linkRecurringTemplateToGoalsMutation = useLinkRecurringTemplateToGoals()
    const createTaskMutation = useCreateTask()
    const archiveGoalMutation = useArchiveGoal()
    const unarchiveGoalMutation = useUnarchiveGoal()
    const achieveGoalMutation = useAchieveGoal()
    const reopenGoalMutation = useReopenGoal()
    const [iteration, setIteration] = useState<TIteration>('scaffold')
    const [isOnboarding, setIsOnboarding] = useState(false)
    const [freshAccount, setFreshAccount] = useState(false)
    const [createdGoalIds, setCreatedGoalIds] = useState<string[]>([])
    const [isFlowOpen, setIsFlowOpen] = useState(false)
    const [consolidationDismissed, setConsolidationDismissed] = useState(false)
    const [mirrorGoalId, setMirrorGoalId] = useState<string | null>(null)
    const [repairGoalId, setRepairGoalId] = useState<string | null>(null)
    const [archiveGoalId, setArchiveGoalIdState] = useState<string | null>(null)
    const [archiveGoalOptions, setArchiveGoalOptions] = useState<TArchiveGoalOptions | null>(null)
    const setArchiveGoalId = useCallback((id: string | null, options?: TArchiveGoalOptions) => {
        setArchiveGoalIdState(id)
        setArchiveGoalOptions(id ? options ?? null : null)
    }, [])
    const [achieveGoalId, setAchieveGoalIdState] = useState<string | null>(null)
    const [achieveGoalOptions, setAchieveGoalOptions] = useState<TAchieveGoalOptions | null>(null)
    const setAchieveGoalId = useCallback((id: string | null, options?: TAchieveGoalOptions) => {
        setAchieveGoalIdState(id)
        setAchieveGoalOptions(id ? options ?? null : null)
    }, [])
    const [justAchievedGoalId, setJustAchievedGoalId] = useState<string | null>(null)
    const clearJustAchievedGoalId = useCallback(() => setJustAchievedGoalId(null), [])
    const persistedGoals = goalsQuery.data ?? []
    const taskGoalLinks = taskLinksQuery.data ?? {}
    const recurringTemplateLinks = recurringTemplateLinksQuery.data ?? {}
    const createdGoals = persistedGoals.filter((goal) => createdGoalIds.includes(goal.id))

    const linkTaskToGoal = useCallback(
        (taskId: string, goalId: string | null, optimisticId?: string, options?: TGoalLinkOptions) => {
            const mutateOptions = {
                onSuccess: (_data: unknown, variables: { id: string; goalId: string | null }) =>
                    options?.onSuccess?.(variables.id),
                onError: (_error: unknown, variables: { id: string; goalId: string | null }) =>
                    options?.onError?.(variables.id),
            }
            if (optimisticId) linkTaskToGoalMutation.mutate({ id: taskId, goalId }, optimisticId, mutateOptions)
            else linkTaskToGoalMutation.mutate({ id: taskId, goalId }, mutateOptions)
        },
        [linkTaskToGoalMutation]
    )
    const createLinkedTask = useCallback(
        (title: string, goalId: string, options?: TLinkedTaskOptions) => {
            const optimisticId = uuidv4()
            createTaskMutation.mutate(
                { title, id_folder: DEFAULT_FOLDER_ID, optimisticId },
                { onError: options?.onCreateError }
            )
            linkTaskToGoal(optimisticId, goalId, optimisticId, { onError: options?.onLinkError })
            return optimisticId
        },
        [createTaskMutation, linkTaskToGoal]
    )
    const onViewGoal = useCallback(
        (goalId: string) => {
            navigate(`/goals/${goalId}`)
        },
        [navigate]
    )

    const updateGoal = useCallback(
        (goalId: string, patch: TGoalPatch, options?: TGoalUpdateOptions) => {
            const goal = persistedGoals.find((candidate) => candidate.id === goalId)
            const normalizedEndDate = patch.endDate === undefined ? undefined : normalizeGoalEndDate(patch.endDate)
            const syncedTimeframe =
                goal && normalizedEndDate !== undefined && patch.timeframeLabel === undefined
                    ? timeframeLabelAfterEndDateEdit(goal.timeframeLabel, normalizedEndDate)
                    : undefined
            updateGoalMutation.mutate(
                {
                    goalId,
                    patch: syncedTimeframe === undefined ? patch : { ...patch, timeframeLabel: syncedTimeframe },
                },
                options
            )
        },
        [persistedGoals, updateGoalMutation]
    )

    const archiveGoal = useCallback(
        (goalId: string, taskHandling: TGoalTaskHandling, options?: TArchiveGoalOptions) => {
            archiveGoalMutation.mutate(
                { goalId, taskHandling, reason: options?.reason },
                { onSuccess: options?.onArchived }
            )
            setArchiveGoalId(null)
        },
        [archiveGoalMutation, setArchiveGoalId]
    )

    // Unlike archive, the dialog stays open until the server answers: success closes it and marks the
    // goal for the list to reveal, failure leaves it open with the choice intact so the user can retry.
    const achieveGoal = useCallback(
        (goalId: string, taskHandling: TGoalTaskHandling, options?: TAchieveGoalOptions) => {
            achieveGoalMutation.mutate(
                { goalId, taskHandling },
                {
                    onSuccess: () => {
                        setJustAchievedGoalId(goalId)
                        setAchieveGoalId(null)
                        options?.onAchieved?.()
                    },
                    onError: options?.onError,
                }
            )
        },
        [achieveGoalMutation, setAchieveGoalId]
    )

    const reopenGoal = useCallback(
        (goalId: string) => {
            reopenGoalMutation.mutate(goalId)
        },
        [reopenGoalMutation]
    )

    const unarchiveGoal = useCallback(
        (goalId: string) => {
            unarchiveGoalMutation.mutate(goalId)
        },
        [unarchiveGoalMutation]
    )

    const createGoal = useCallback(
        (draft: TGoalDraft) => {
            const goal = buildGoalFromDraft(draft)
            setIsFlowOpen(false)
            const plannedTaskTitles = plannedTaskTitlesForDraft(draft)
            createGoalMutation.mutate(goal, {
                onSuccess: (saved) => {
                    setCreatedGoalIds((ids) => [...ids, saved.id])
                    setMirrorGoalId((current) => (current === goal.id ? saved.id : current))
                    // One-offs are the plan's concrete work, so they land where work lives and
                    // link to the goal — the same pair of calls CreateTaskDialog makes.
                    plannedTaskTitles.forEach((title) => createLinkedTask(title, saved.id))
                    draft.recurringTemplateIds?.forEach((templateId) => {
                        const goalIds = addGoalToTemplateHook(recurringTemplateLinks[templateId], saved.id)
                        linkRecurringTemplateToGoalsMutation.mutate({ id: templateId, goalIds })
                    })
                    // Linear-style two-line confirmation: check + "Goal created" + goal title.
                    toast.custom(
                        (t) => (
                            <div
                                style={{ boxShadow: '0 1px 2px rgb(0 0 0 / 0.04), 0 8px 24px rgb(0 0 0 / 0.12)' }}
                                className={cn(
                                    'pointer-events-auto flex w-[336px] items-start gap-3 rounded-xl border border-border bg-card p-3.5',
                                    'transition-[opacity,transform] duration-200 ease-out',
                                    t.visible ? 'translate-y-0 opacity-100' : 'translate-y-1 opacity-0'
                                )}
                            >
                                <div className="mt-px flex size-5 shrink-0 items-center justify-center rounded-full bg-success">
                                    <Check className="size-3 text-white" strokeWidth={3} />
                                </div>
                                <div className="min-w-0 flex-1">
                                    <p className="text-body-sm font-medium text-foreground">Goal created</p>
                                    <p className="mt-0.5 truncate text-body-sm text-muted-foreground">{saved.title}</p>
                                    {plannedTaskTitles.length > 0 && (
                                        <p className="mt-0.5 text-body-sm text-muted-foreground">
                                            {plannedTaskTitles.length === 1
                                                ? '1 task added'
                                                : `${plannedTaskTitles.length} tasks added`}
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            onViewGoal(saved.id)
                                            toast.dismiss(t.id)
                                        }}
                                        className="mt-2 text-body-sm font-medium text-primary transition-transform active:scale-[0.96]"
                                    >
                                        View goal
                                    </button>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => toast.dismiss(t.id)}
                                    aria-label="Dismiss"
                                    className="-mr-1 -mt-1 flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-[background-color,color,transform] duration-150 hover:bg-muted hover:text-foreground active:scale-[0.96]"
                                >
                                    <X className="size-3.5" />
                                </button>
                            </div>
                        ),
                        { duration: 4000 }
                    )
                },
            })
            return goal
        },
        [createGoalMutation, createLinkedTask, linkRecurringTemplateToGoalsMutation, onViewGoal, recurringTemplateLinks]
    )

    const logGoalProgress = useCallback(
        (goalId: string, log: Omit<TGoalLog, 'goalId' | 'id'>) => {
            logGoalProgressMutation.mutate({ goalId, id: uuidv4(), ...log })
            toast.success('Progress logged')
        },
        [logGoalProgressMutation]
    )

    const isReadOnly = useIsReadOnly()

    const resetDemo = useCallback(() => {
        toast.dismiss()
        setCreatedGoalIds([])
        setIsFlowOpen(false)
        setIsOnboarding(false)
        setFreshAccount(false)
        setConsolidationDismissed(false)
        setMirrorGoalId(null)
        setRepairGoalId(null)
    }, [])

    const value = useMemo<TGoalCreation>(
        () => ({
            iteration,
            setIteration: (i: TIteration) => {
                setIteration(i)
                setIsFlowOpen(false)
            },
            isOnboarding,
            setIsOnboarding,
            freshAccount,
            setFreshAccount,
            createdGoals,
            allGoals: freshAccount ? createdGoals : persistedGoals,
            areGoalsLoading: goalsQuery.isLoading,
            areGoalsUnavailable: goalsQuery.isError,
            createGoal,
            logGoalProgress,
            isFlowOpen,
            openFlow: () => (isReadOnly ? notifyReadOnlyRejection() : setIsFlowOpen(true)),
            closeFlow: () => setIsFlowOpen(false),
            consolidationDismissed,
            dismissConsolidation: () => setConsolidationDismissed(true),
            mirrorGoalId,
            setMirrorGoalId,
            updateGoal,
            isSavingGoal: updateGoalMutation.isLoading,
            goalSaveFailed: updateGoalMutation.isError,
            repairGoalId,
            setRepairGoalId,
            archiveGoal,
            unarchiveGoal,
            archiveGoalId,
            archiveGoalOptions,
            setArchiveGoalId,
            achieveGoal,
            isAchievingGoal: achieveGoalMutation.isLoading,
            reopenGoal,
            achieveGoalId,
            achieveGoalOptions,
            setAchieveGoalId,
            justAchievedGoalId,
            clearJustAchievedGoalId,
            taskGoalLinks,
            isLinkingTaskToGoal: linkTaskToGoalMutation.isLoading,
            linkTaskToGoal,
            goalIdForTask: (taskId: string, optimisticId?: string) =>
                taskGoalLinks[taskId] ?? (optimisticId ? taskGoalLinks[optimisticId] : undefined),
            createLinkedTask,
            resetDemo,
        }),
        [
            isReadOnly,
            createLinkedTask,
            taskGoalLinks,
            linkTaskToGoalMutation.isLoading,
            linkTaskToGoal,
            persistedGoals,
            goalsQuery.isLoading,
            goalsQuery.isError,
            iteration,
            isOnboarding,
            freshAccount,
            createdGoals,
            isFlowOpen,
            consolidationDismissed,
            mirrorGoalId,
            repairGoalId,
            archiveGoal,
            unarchiveGoal,
            archiveGoalId,
            archiveGoalOptions,
            setArchiveGoalId,
            achieveGoal,
            achieveGoalMutation.isLoading,
            reopenGoal,
            achieveGoalId,
            achieveGoalOptions,
            setAchieveGoalId,
            justAchievedGoalId,
            clearJustAchievedGoalId,
            createGoal,
            logGoalProgress,
            updateGoal,
            updateGoalMutation.isLoading,
            updateGoalMutation.isError,
            resetDemo,
        ]
    )
    // With the goals flag off the context reads as absent, so goal pickers, tags and toasts on tasks
    // render nothing. The provider itself stays mounted so toggling the flag never remounts the page.
    return <Ctx.Provider value={areGoalsEnabled ? value : null}>{children}</Ctx.Provider>
}
